import React, { useEffect, useRef, useState } from "react";
import CustomButton from "components/common/Button/CustomButton";
import FileUploadInput from "components/common/FileUpload/FileUploadInput";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { USER_ROLE, DOCUMENT_CONSTANTS } from "Constants/constant";
import { apiGetRequest } from "helpers/Requests";
import { roleAccess } from "helpers/RolesPermission";
import { toast } from "react-toastify";
import { apiCall } from "store/services/service";
import { BASE_URL } from "store/services/URL";
import {
  getUrlRole,
  getFileNameValidationError,
  mapDocumentUploadErrorMessage,
} from "Utils/utils";
import Select from "../../../../../components/common/Select/Select";
import { connect } from "react-redux";
import { getUser } from "../../../../../store/selector/auth.selector";
import { useForm } from "react-hook-form";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import moment from "moment";

/** Participant docs: visibility false = shown to worker / visible in Frontline lists. */
const getDefaultParticipantDocVisibility = (role) => {
  if (role !== "participant") return true;
  return roleAccess([USER_ROLE.Sp_Standard_User]) ? false : true;
};

const toExpiryIso = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    const dateOnly = value.trim().substring(0, 10);
    const year = Number(dateOnly.slice(0, 4));
    if (year < 1900 || year > 9999) return null;
    return `${dateOnly}T00:00:00.000Z`;
  }
  const parsed = moment(value);
  if (!parsed.isValid()) return null;
  const year = parsed.year();
  if (year < 1900 || year > 9999) return null;
  return `${parsed.format("YYYY-MM-DD")}T00:00:00.000Z`;
};

const validateExpiryWhenEnabled = (showInput, expiryDate) => {
  if (!showInput) {
    return { ok: true, expiry_time: null };
  }
  const iso = toExpiryIso(expiryDate);
  if (!iso) {
    return {
      ok: false,
      message: "Please enter a valid expiry date in DD/MM/YYYY format.",
    };
  }
  if (moment(iso).startOf("day").isBefore(moment().startOf("day"))) {
    return {
      ok: false,
      message: "Expiry date must be a future date.",
    };
  }
  return { ok: true, expiry_time: iso };
};

const UploadNewFileModel = (props) => {
  const {
    role,
    handleClose,
    setUpdatedFile,
    isOpen,
    workerId,
    parentId,
    currentLoginUser,
    isUserGracePeriodOver,
    // documentList,
  } = props;

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [isFileUploadToS3, setIsFileUploadToS3] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [visibility, setVisibility] = useState(() =>
    getDefaultParticipantDocVisibility(role)
  );
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [isDocTypeLoader, setIsDocTypeLoader] = useState(false);
  /** Reuse S3 key on retry (same file) so Upload can re-hit register API without re-arming vapor effect. */
  const vaporPayloadRef = useRef(null);

  const {
    handleSubmit,
    reset,
  } = useForm();

  useEffect(() => {
    if (!isOpen) return;
    getFileTypes();
    if (role === "sil") {
      setSelectedFileType({ label: "Sil Documents", value: 2 });
    }
    if (role === "participant") {
      setVisibility(getDefaultParticipantDocVisibility(role));
    }
  }, [isOpen, role]);

  const modalFooter = () => (
    <>
      <CustomButton
        label='Cancel'
        variant='secondary'
        clickHandler={() => {
          handleClear();
          setFile("");
          setSelectedDocument("");
          setShowInput(false);
          setFileName("");
          handleClose(false);
        }}
      />
      <CustomButton
        showLoading={isLoading}
        isDisabled={
          (file == null || file === "" || selectedFileType === null
            ? true
            : isLoading) ||
          isUserGracePeriodOver ||
          !!getFileNameValidationError(fileName?.trim())
        }
        label='Upload'
        variant='primary'
        clickHandler={handleSubmitForFile}
      />
    </>
  );

  const resetUploadLoadingState = () => {
    setIsLoading(false);
    setIsFileUploadToS3(false);
  };

  const handleFileUploadBaseVapour = async (data) => {
    if (data?.key) {
      vaporPayloadRef.current = {
        key: data.key,
        ext: data.ext,
        fileFingerprint: file ? `${file.name}-${file.size}` : null,
      };
    }

    const nameError = getFileNameValidationError(fileName?.trim());
    if (nameError) {
      toast.error(nameError);
      resetUploadLoadingState();
      return;
    }

    const expiryCheck = validateExpiryWhenEnabled(showInput, expiryDate);
    if (!expiryCheck.ok) {
      toast.error(expiryCheck.message);
      resetUploadLoadingState();
      return;
    }

    Object.assign(data, {
      name: fileName.trim(),
      file_type: data.ext,
      document_type_id: selectedFileType?.value,
      with_expiry: showInput,
      label: "",
      note: "xyz",
      expiry_time: expiryCheck.expiry_time,
      folder_structure_id: parentId,
      visibility: visibility,
    });

    if (role === "sil") {
      Object.assign(data, {
        sil_home_id: workerId,
      });
    } else {
      Object.assign(data, {
        worker_id: workerId,
      });
    }

    const Role = roleAccess([USER_ROLE.Sp_Standard_User]) ? "hrm" : "sp";
    const URL = `${BASE_URL + Role + "/file-management/upload-document"}`;
    const URLNew = `${
      BASE_URL + `${Role}/file-management/participant/upload-document`
    }`;
    if (role === "participant") {
      data = { ...data, pt_id: data.worker_id };
    }

    if (role === "sil") {
      // Saga also toasts on success; skipToast so handleFinish shows one message.
      setUpdatedFile({ ...data, skipToast: true }, (response, error) => {
        if (error) {
          resetUploadLoadingState();
          toast.error(mapDocumentUploadErrorMessage(error));
          return;
        }
        if (response) {
          handleFinish(response);
          setShowInput(false);
          return;
        }
        resetUploadLoadingState();
        toast.error("Upload failed. Please try again.");
      });
      return;
    }

    try {
      const response = await apiCall.post({
        url: role === "participant" ? URLNew : URL,
        params: data,
        isAuthToken: currentLoginUser?.AccessToken,
      });
      handleFinish(response);
      setShowInput(false);
    } catch (e) {
      console.error("Error uploading document:", e);
      toast.error(mapDocumentUploadErrorMessage(e));
    } finally {
      resetUploadLoadingState();
    }
  };

  const handleClear = () => {
    vaporPayloadRef.current = null;
    setVisibility(getDefaultParticipantDocVisibility(role));
    setSelectedFileType("");
    setFile("");
    setFileName("");
    setShowInput(false);
    setExpiryDate(null);
  };

  const handleFinish = (data) => {
    const doc = data?.data ?? data;
    const uploadOk =
      doc &&
      (doc.file_type || doc.id || doc.uuid || typeof data?.message === "string");

    if (!uploadOk) {
      toast.error("Upload failed. Please try again.");
      resetUploadLoadingState();
      return;
    }

    resetUploadLoadingState();

    if (role !== "sil" && typeof setUpdatedFile === "function") {
      // Reducer expects action.payload.data.data (same shape as CaseNotesFileModal / EditModel).
      if (data?.data && (data.data.file_type || data.data.id || data.data.uuid)) {
        setUpdatedFile({ data });
      } else if (doc && (doc.file_type || doc.id || doc.uuid)) {
        setUpdatedFile({ data: { data: doc } });
      }
    }

    toast.success("Document uploaded successfully");
    handleClose(false);
    handleClear();
    setFile("");
    setSelectedDocument("");
    setFileName("");
    setShowInput(false);
    setVisibility(getDefaultParticipantDocVisibility(role));
  };

  const handleError = () => {
    resetUploadLoadingState();
  };

  const handleSubmitForFile = async () => {
    if (isUserGracePeriodOver) {
      return;
    }

    const expiryCheck = validateExpiryWhenEnabled(showInput, expiryDate);
    if (!expiryCheck.ok) {
      toast.error(expiryCheck.message);
      return;
    }

    setIsLoading(true);

    const fingerprint = file ? `${file.name}-${file.size}` : null;
    const cached = vaporPayloadRef.current;
    if (
      cached?.key &&
      file &&
      cached.fileFingerprint === fingerprint
    ) {
      handleFileUploadBaseVapour({
        key: cached.key,
        ext: cached.ext,
        name: file.name,
      });
      return;
    }

    setIsFileUploadToS3(false);
    queueMicrotask(() => setIsFileUploadToS3(true));
  };

  const getFileTypes = async () => {
    try {
      setIsDocTypeLoader(true);
      const res = await apiGetRequest(`${getUrlRole()}/doc-types`);
      if (res.status === 200) {
        const options = res.data.doc_types.map((doc) => {
          const obj = {
            label: doc.name,
            value: doc.id,
          };
          return obj;
        });
        setFileTypeOptions([...options]);
      }
      setIsDocTypeLoader(false);
    } catch (err) {
      console.log("err", err);
      setIsDocTypeLoader(false);
    }
  };

  return (
    <>
      <ModalSkeleton
        title='Upload New File'
        cssClass='w-[35%]'
        modalFooter={modalFooter()}
        isOpen={isOpen}
        closeModal={() => {
          setVisibility(true);
          handleClose(false);
          handleClear();
          setShowInput(false);
          setFile("");
          setSelectedDocument("");
          setFileName("");
        }}
      >
        <div className='flex flex-col'>
          <div className='grid grid-cols-2 gap-x-4'>
            <div className='col-span-2'>
              <div className='space-y-4'>
                <div>
                  <label
                    htmlFor='first_name'
                    className='text-start did-floating-label'
                  >
                    File Name
                  </label>
                  <input
                    type='text'
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    className='did-floating-input capitalize'
                  />
                  {getFileNameValidationError(fileName?.trim()) && (
                    <span className='text-sm text-red-500 mt-1 block'>
                      {getFileNameValidationError(fileName.trim())}
                    </span>
                  )}
                </div>
                {role !== "sil" && (
                  <div className='mt-4 flex items-start justify-start space-x-2'>
                    <div className='did-floating-label-content w-full'>
                      <label
                        htmlFor='interview-questions'
                        className=' text-start did-floating-label'
                      >
                        Category
                      </label>
                      <Select
                        isDisabled={isDocTypeLoader}
                        loading={isDocTypeLoader}
                        cssClass='z-20'
                        isSearch={false}
                        data={fileTypeOptions}
                        selectedOption={selectedFileType}
                        handleChange={(e) => setSelectedFileType(e)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {role === "participant" && (
              <div className='col-span-2 space-x-4'>
                <label className=' text-start did-floating-label'>
                  Show to Worker
                </label>
                <input
                  type='checkbox'
                  checked={!visibility}
                  onChange={() => setVisibility((prev) => !prev)}
                  className='rounded '
                />
              </div>
            )}
            <div className='col-span-2 space-x-4'>
              <label className=' text-start did-floating-label'>
                Set Expiry Date
              </label>
              <input
                type='checkbox'
                checked={showInput}
                onChange={() => {
                  setShowInput((prev) => {
                    const next = !prev;
                    if (!next) setExpiryDate(null);
                    return next;
                  });
                }}
                className='rounded '
              />
            </div>

            {showInput && (
              <div className='col-span-2'>
                <label
                  htmlFor='expiry_date'
                  className='text-start did-floating-label'
                >
                  Expiry Date
                </label>
             <DateRangePickerNew
                          isSingleDate
                          isClearable={false}
                          handleCancel={()=>setExpiryDate(null)}
                       showCalendarIcon
                          preventOpenOnFocus
                          handleChange={(obj) => {
                            setExpiryDate(obj);
                          }}
                          singleSelectedDate={expiryDate && new Date(expiryDate)}
                          minDate={moment().startOf("day").toDate()}
                        />
              </div>
            )}

            {!selectedDocument && (
              <div className='col-span-2 mt-4'>
                <FileUploadInput
                  type='any'
                  handleFile={(e) => {
                    const maxSizeMB = DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB;
                    const maxSizeBytes = DOCUMENT_CONSTANTS.MAX_FILE_SIZE_BYTES;
                    if (e.size > maxSizeBytes) {
                      toast.error(
                        `File size should not exceed ${maxSizeMB} MB.`
                      );
                      return;
                    }

                    vaporPayloadRef.current = null;
                    setFile(e);
                  }}
                  size={String(DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB)}
                  file={file}
                  handleDelete={() => {
                    vaporPayloadRef.current = null;
                    setFile("");
                  }}
                  isDisabled={isLoading}
                  isVapourFileUpload={true} 
                  isFileUploadToS3={isFileUploadToS3}
                  handleFileUploadBaseVapour={handleFileUploadBaseVapour}
                  handleError={handleError}
                />
              </div>
            )}
          </div>
        </div>
      </ModalSkeleton>
    </>
  );
};

const mapStateToProps = (state) => {
  const currentLoginUser = getUser(state);

  return { currentLoginUser };
};

export default connect(mapStateToProps, null)(UploadNewFileModel);
